import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type OutreachContactDecision, outreachApi, outreachContactDecisionsQueryKey } from '../api'

// The founder's pre-launch approve/decline decisions, persisted server-side so
// they survive a refresh and are applied when the campaign launches (declined
// excluded, approved pre-queued). Only meaningful before launch — once the
// campaign is live, the real OutreachLead queue is the source of truth.
export function useOutreachContactDecisionsQuery(
  startupId: string,
  queryOptions?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: outreachContactDecisionsQueryKey(startupId),
    queryFn: () => outreachApi.getContactDecisions(startupId),
    ...queryOptions,
  })
}

// Upsert one or more decisions in a single request — a per-contact approve/
// decline sends one, "Approve all" sends every still-pending contact. Updates
// the cache optimistically so the board's badges flip instantly, rolling back
// if the request fails and reconciling with the server once it settles.
export function useSetOutreachContactDecisionsMutation(startupId: string) {
  const queryClient = useQueryClient()
  const queryKey = outreachContactDecisionsQueryKey(startupId)
  return useMutation({
    mutationFn: (decisions: OutreachContactDecision[]) =>
      outreachApi.setContactDecisions(startupId, decisions),
    onMutate: async (decisions: OutreachContactDecision[]) => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<OutreachContactDecision[]>(queryKey)
      queryClient.setQueryData<OutreachContactDecision[]>(queryKey, (prev) => {
        const byKey = new Map((prev ?? []).map((d) => [`${d.investorId}:${d.contactId}`, d]))
        for (const d of decisions) byKey.set(`${d.investorId}:${d.contactId}`, d)
        return [...byKey.values()]
      })
      return { previous }
    },
    onError: (_err, _decisions, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}
